import React, { Suspense, useRef } from "react";
import { Link } from "react-router-dom";
import { useScroll, useMotionValueEvent } from "framer-motion";
import { useState } from "react";
import { ArrowRight, ChevronDown } from "lucide-react";

import { PageSeo } from "@/components/seo/PageSeo";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { MaskReveal } from "@/components/motion/MaskReveal";
import { Reveal } from "@/components/motion/Reveal";
import { Stagger, StaggerItem } from "@/components/motion/Stagger";
import { useT } from "@/hooks/useT";
import { useLocale } from "@/hooks/useLocale";
import { addLocalePrefix } from "@/lib/locale";

const SceneB = React.lazy(() => import("@/components/scenes/SceneB"));

function useMotionValueState(mv, initial = 0) {
  const [value, setValue] = useState(initial);
  useMotionValueEvent(mv, "change", (v) => setValue(v));
  return value;
}

function SceneFallback() {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        background: "var(--surface-base)",
      }}
    />
  );
}

const LAYER_HREFS = [
  "/platform/marketing",
  "/platform/ai",
  "/platform/data",
  "/platform/energy",
];

function StackDiagram() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const progress = useMotionValueState(scrollYProgress, 0);
  const { t } = useT("platform");
  const locale = useLocale();
  const layers = t("layers", { returnObjects: true }) || [];
  const exploreCta = t("exploreCta");

  return (
    <section
      ref={ref}
      className="relative"
      data-testid="platform-stack-diagram"
    >
      <div className="relative min-h-[220vh]">
        <div className="sticky top-0 flex min-h-screen items-center py-16">
          <div className="container-frac">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[3fr_2fr]">
              <div
                className="relative h-[70vh] overflow-hidden rounded-[16px] border border-hairline"
                data-testid="platform-scene-b-wrapper"
              >
                <Suspense fallback={<SceneFallback />}>
                  <SceneB
                    scrollProgress={progress}
                    style={{ position: "absolute", inset: 0 }}
                  />
                </Suspense>
              </div>
              <div className="flex flex-col justify-center gap-6">
                {layers.map((layer, idx) => (
                  <Card
                    key={layer.number}
                    variant="glass"
                    className="transition-opacity duration-base"
                    data-testid={`platform-layer-${layer.number}`}
                    style={{ opacity: progress > 0.15 ? 1 : 0.35 }}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-caps text-eyebrow">
                          {layer.number}
                        </div>
                        <h3 className="mt-1 text-base font-bold text-ink-primary">
                          {layer.name}
                        </h3>
                      </div>
                      {idx === 3 && (
                        <Badge status="ROADMAP" onDark />
                      )}
                    </div>
                    <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-secondary">
                      {(layer.items || []).map((i) => (
                        <li key={i} className="before:mr-1.5 before:content-['·']">
                          {i}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={addLocalePrefix(LAYER_HREFS[idx], locale)}
                      className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-eyebrow hover:text-ink-primary"
                      data-testid={`platform-layer-${layer.number}-cta`}
                    >
                      {exploreCta}
                      <ArrowRight className="h-3 w-3" aria-hidden />
                    </Link>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Flywheel() {
  const { t } = useT("platform");
  const nodes = t("flywheel.nodes", { returnObjects: true }) || [];
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="platform-flywheel"
    >
      <div className="max-w-3xl">
        <Reveal>
          <span className="eyebrow">{t("flywheel.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("flywheel.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("flywheel.body")}
          </p>
        </Reveal>
      </div>

      <Stagger
        className="mt-12 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-stretch"
        gap={100}
        data-testid="platform-flywheel-nodes"
      >
        {nodes.map((node, i) => (
          <StaggerItem key={i} className="flex flex-1 items-stretch">
            <div className="flex items-center gap-2 md:flex-1">
              <Card variant="glass" className="flex-1 min-w-[180px]">
                <div className="text-[10px] font-semibold uppercase tracking-caps text-eyebrow">
                  {`0${i + 1}`}
                </div>
                <p className="mt-2 text-sm font-semibold leading-snug text-ink-primary">
                  {node}
                </p>
              </Card>
              {i < nodes.length - 1 && (
                <>
                  <ArrowRight
                    className="hidden shrink-0 text-frac-violet md:block"
                    aria-hidden
                  />
                  <ChevronDown
                    className="mx-auto shrink-0 text-frac-violet md:hidden"
                    aria-hidden
                  />
                </>
              )}
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}

function RailsPreview() {
  const { t } = useT("platform");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="platform-rails-preview"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("rails.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("rails.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("rails.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/platform/tokenization", locale)}
              variant="secondary-on-dark"
              size="md"
              data-testid="platform-rails-cta"
            >
              {t("rails.cta")}
              <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function IntelligencePreview() {
  const { t } = useT("platform");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="platform-intelligence-preview"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow">{t("intelligencePreview.eyebrow")}</span>
        </Reveal>
        <MaskReveal
          as="h2"
          className="mt-4 text-3xl font-bold tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("intelligencePreview.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("intelligencePreview.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/intelligence", locale)}
              variant="secondary-on-dark"
              size="md"
              data-testid="platform-intelligence-cta"
            >
              {t("intelligencePreview.cta")}
              <ArrowRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Hero() {
  const { t } = useT("platform");
  return (
    <section
      className="container-frac pt-24 pb-16 md:pt-32 md:pb-24"
      data-testid="platform-hero"
    >
      <div className="max-w-4xl">
        <Reveal>
          <span className="eyebrow" data-testid="page-eyebrow">
            {t("hero.eyebrow")}
          </span>
        </Reveal>
        <MaskReveal
          as="h1"
          className="mt-6 text-4xl font-bold leading-tight tracking-tight text-ink-primary sm:text-5xl lg:text-6xl"
          data-testid="page-h1"
        >
          {t("hero.h1")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-8 text-lg leading-normal text-ink-secondary">
            {t("hero.body")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  const { t } = useT("platform");
  const locale = useLocale();
  return (
    <section
      className="container-frac py-24 md:py-32"
      data-testid="platform-final-cta"
    >
      <div className="mx-auto max-w-3xl text-center">
        <MaskReveal
          as="h2"
          className="mx-auto text-3xl font-bold leading-tight tracking-tight text-ink-primary sm:text-4xl lg:text-5xl"
        >
          {t("finalCta.h2")}
        </MaskReveal>
        <Reveal delay={200}>
          <p className="mt-6 text-lg leading-normal text-ink-secondary">
            {t("finalCta.body")}
          </p>
        </Reveal>
        <Reveal delay={350}>
          <div className="mt-10">
            <Button
              as={Link}
              to={addLocalePrefix("/contact", locale)}
              variant="primary-on-dark"
              size="lg"
              data-testid="platform-final-cta-primary"
            >
              {t("finalCta.cta")}
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Platform() {
  return (
    <>
      <PageSeo
        title="The Frac Stack — Four Layers, One Asset Class"
        description="The full stack for capturing behavioural data privately, structuring it with AI, and tokenizing it into units that can be priced, purchased and accounted for."
        path="/platform"
        image="/og/platform.png"
      />
      <div data-testid="page-platform">
        <Hero />
        <StackDiagram />
        <Flywheel />
        <RailsPreview />
        <IntelligencePreview />
        <FinalCta />
      </div>
    </>
  );
}
